import React, {forwardRef, memo, ReactNode, useMemo} from "react";

export type AvatarSize = "xs" | "sm" | "md" | "lg" | "xl" | number;

export interface AvatarProps extends React.ImgHTMLAttributes<HTMLImageElement> {
    size?: AvatarSize;
    src?: string;
    name?: string;
    alt?: string;
    showBorder?: boolean;
    status?: "online" | "offline" | "away" | "busy" | null;
    className?: string;
    icon?: ReactNode;
    backgroundColor?: string;
}

class AvatarConfig {
    static readonly SIZE_MAP: Record<string, number> = {
        xs: 20,
        sm: 28,
        md: 40,
        lg: 64,
        xl: 96,
    };

    static getPixelSize(size: AvatarSize): number {
        return typeof size === "number" ? size : this.SIZE_MAP[size] ?? this.SIZE_MAP.md;
    }

    static getStatusColor(status: AvatarProps["status"]): string {
        switch (status) {
            case "online":
                return "bg-green-400 ring-2 ring-white";
            case "away":
                return "bg-yellow-400 ring-2 ring-white";
            case "busy":
                return "bg-red-500 ring-2 ring-white";
            case "offline":
            default:
                return "bg-gray-300 ring-2 ring-white";
        }
    }

    static getFontSize(pixelSize: number): number {
        return Math.max(10, Math.floor(pixelSize / 2.6));
    }
}

class AvatarUtils {
    static getInitials(name?: string): string {
        if (!name) return "";
        const parts = name.trim().split(/\s+/);
        if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }

    static getAltText(alt?: string, name?: string): string {
        return alt ?? name ?? "avatar";
    }

    static getAriaLabel(alt?: string, name?: string): string {
        return alt ?? name ?? "avatar";
    }
}

const DefaultAvatarSvg: React.FC = memo(() => (
    <svg viewBox="0 0 24 24" width="48" height="48" aria-hidden>
        <path
            d="M12 12c2.761 0 5-2.239 5-5s-2.239-5-5-5-5 2.239-5 5 2.239 5 5 5zm0 2c-4.418 0-8 3.582-8 8h2c0-3.309 2.691-6 6-6s6 2.691 6 6h2c0-4.418-3.582-8-8-8z"/>
    </svg>
))

// Avatar content renderer
class AvatarContentRenderer {
    static renderContent(
        src: string | null,
        icon: ReactNode,
        initials: string,
        pixelSize: number,
        altText: string,
        imgProps: React.ImgHTMLAttributes<HTMLImageElement>
    ): ReactNode {
        if (src) {
            return (
                <img
                    src={src}
                    alt={altText}
                    className="w-full h-full object-cover"
                    {...imgProps}
                />
            );
        }

        if (icon) {
            return (
                <div className="w-full h-full flex items-center justify-center">
                    {icon}
                </div>
            );
        }

        return (
            <div
                className="w-full h-full flex items-center justify-center text-gray-700 font-medium"
                style={{fontSize: AvatarConfig.getFontSize(pixelSize)}}
            >
                {initials || <DefaultAvatarSvg/>}
            </div>
        );
    }
}

export const AvatarComponent = forwardRef<HTMLDivElement, AvatarProps>(
    (
        {
            size = "md",
            src = null,
            name,
            alt,
            showBorder = false,
            status = null,
            className = "",
            icon,
            backgroundColor = "bg-gray-100",
            ...imgProps
        },
        ref
    ) => {
        // Memoized computations
        const pixelSize = useMemo(() =>
                AvatarConfig.getPixelSize(size),
            [size]
        );

        const initials = useMemo(() =>
                AvatarUtils.getInitials(name),
            [name]
        );

        const altText = useMemo(() =>
                AvatarUtils.getAltText(alt, name),
            [alt, name]
        );

        const ariaLabel = useMemo(() =>
                AvatarUtils.getAriaLabel(alt, name),
            [alt, name]
        );

        const statusColor = useMemo(() =>
                AvatarConfig.getStatusColor(status),
            [status]
        );

        const wrapperStyle: React.CSSProperties = useMemo(() =>
                ({width: pixelSize, height: pixelSize}),
            [pixelSize]
        );

        const avatarContent = useMemo(() =>
                AvatarContentRenderer.renderContent(src, icon, initials, pixelSize, altText, imgProps),
            [src, icon, initials, pixelSize, altText, imgProps]
        );

        // CSS class builders
        const borderClass = showBorder ? "ring-1 ring-gray-200" : "";
        const avatarContainerClass = `flex items-center justify-center rounded-full overflow-hidden select-none ${borderClass} ${backgroundColor}`;

        return (
            <div
                ref={ref}
                role={alt ? undefined : "img"}
                aria-label={ariaLabel}
                className={`relative inline-flex items-center justify-center ${className}`}
                style={wrapperStyle}
            >
                <div
                    className={avatarContainerClass}
                    style={{width: pixelSize, height: pixelSize}}
                >
                    {avatarContent}
                </div>

                {status && (
                    <span
                        className={`absolute bottom-0 right-0 translate-x-1/4 translate-y-1/4 rounded-full w-3 h-3 md:w-3 md:h-3 ${statusColor}`}
                        aria-hidden
                    />
                )}
            </div>
        );
    }
);

AvatarComponent.displayName = "AvatarComponent";